/* ===================================================================
   TrialBar -- the free-trial countdown bar pinned to the top of the main
   area. Mounted ONCE in AppShell (above the topbar), so it shows on EVERY
   dashboard route -- global, not per-screen.

   2026-07-30 (Brad, Steve ask): the countdown had gone "nowhere to be
   found". ROOT CAUSE was the gating, not the mount: the bar required a
   non-empty non-'pro' `tier` AND a deadline, but the backend
   (_derive_trial_ends_at in engine/api/v1/core.py) emits a REAL
   trial_ends_at for brand-new trialing clients whose `tier` is still
   null/empty -- so the tier check hid the bar during exactly the trial it
   was meant to count. Fix: gate SOLELY on a real trial_ends_at. The backend
   already only emits that timestamp while the client is genuinely trialing
   (real signup + 14 days; null once paid/expired), so its presence IS the
   authoritative "is trialing" signal -- no separate tier gate needed, and
   no fabricated deadline is ever possible.

   Steve also wants it BOLD + HIGH-CONTRAST and PERSISTENT (older tradesman,
   glary phone), so the old soft pale-blue "calm" band is retired: the live
   bar is now a bold warm band shown the whole trial. Copy per Steve:
     - >= 1 day left ....... "X days left in your free trial"
     - under a day ......... "X hours left in your free trial"
     - final hour .......... "X minutes left in your free trial"
     - expired ............. red band + "Choose a plan"
   A gentle pulsing dot is added only inside the final day.

   REAL-STATE GATING (Hard Rule: no fabricated countdown). Drives purely off
   me.trial_ends_at; hides gracefully if the backend doesn't send it.

   Ticks once a minute (minutes is the finest unit shown).
   =================================================================== */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe } from '../../hooks';
import '../../styles/overlays.css';

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
// Inside the final day, add the pulsing dot for a touch of urgency.
const FINAL_DAY_MS = DAY_MS;

export function TrialBar() {
  const navigate = useNavigate();
  const { data: me } = useMe();

  // Re-render each minute so the countdown stays live (minutes is the finest
  // unit shown). A per-second tick would be wasted work here.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // ---- REAL gating: show whenever the backend reports a real trial window ----
  // trial_ends_at is present ONLY while genuinely trialing (server-derived from
  // the real signup date), so its presence alone is the trial signal. We do NOT
  // additionally gate on tier -- a brand-new trialing client often has an empty
  // tier, and the old `tier !== ''` check hid the bar for exactly those clients.
  const endMs = me?.trial_ends_at ? Date.parse(me.trial_ends_at) : NaN;
  if (!Number.isFinite(endMs)) return null;

  // Deep-link "Upgrade" straight to the Billing section.
  const toBilling = () => navigate('/settings#billing');

  // ---- EXPIRED state ----
  if (endMs <= now) {
    return (
      <div className="rh-trial rh-trial--expired">
        <div className="rh-trial__left">
          <span className="rh-trial__pill-lead">Your free trial has ended.</span>
          <span className="rh-trial__pill-sub">
            Choose a plan to keep collecting reviews.
          </span>
        </div>
        <button className="rh-trial__cta" onClick={toBilling}>
          Choose a plan &rarr;
        </button>
      </div>
    );
  }

  // ---- LIVE countdown: days, then hours under a day, then minutes ----
  const remMs = endMs - now;
  let label: string;
  if (remMs >= DAY_MS) {
    // Round UP so a trial with 6 days + a few hours reads "7 days left" rather
    // than undercounting the time the owner actually has.
    const days = Math.ceil(remMs / DAY_MS);
    label = `${days} ${days === 1 ? 'day' : 'days'} left in your free trial`;
  } else if (remMs >= HOUR_MS) {
    const hours = Math.max(1, Math.floor(remMs / HOUR_MS));
    label = `${hours} ${hours === 1 ? 'hour' : 'hours'} left in your free trial`;
  } else {
    const mins = Math.max(1, Math.ceil(remMs / 60_000));
    label = `${mins} ${mins === 1 ? 'minute' : 'minutes'} left in your free trial`;
  }

  const finalDay = remMs <= FINAL_DAY_MS;

  return (
    <div className={'rh-trial rh-trial--live' + (finalDay ? ' rh-trial--urgent' : '')}>
      <div className="rh-trial__left">
        {finalDay && <span className="rh-trial__dot" aria-hidden="true" />}
        <span className="rh-trial__lead">{label}</span>
      </div>
      <button className="rh-trial__cta" onClick={toBilling}>
        Upgrade &rarr;
      </button>
    </div>
  );
}
